import React, { useState, useId } from 'react';

interface InlineTrendProps {
  /** null entries are weeks with no data — dropped, never drawn as 0. */
  values: Array<number | null>;
  goal?: number;
  color: string;
  formatValue?: (v: number) => string;
  selectedIndex?: number;
  weekLabels?: string[];
}

const W = 195;
const H = 72;
const PAD = { top: 14, right: 14, bottom: 16, left: 30 };


function smoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

const InlineTrend: React.FC<InlineTrendProps> = ({
  values: rawValues,
  goal,
  color,
  formatValue,
  selectedIndex: rawSelectedIndex,
  weekLabels: rawWeekLabels,
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const reactId = useId();
  const gradId = `trend-grad${reactId}`;
  const glowId = `trend-glow${reactId}`;
  const fmt = formatValue ?? ((v: number) => `${v.toFixed(1)}%`);
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  // Weeks without data are omitted from the series (a sparkline shows what
  // happened; plotting 0 for "no data" invents a dip). Labels stay aligned to
  // the weeks that remain, and the selected-week highlight follows.
  const present: Array<{ value: number; index: number }> = [];
  (rawValues ?? []).forEach((value, index) => {
    if (typeof value === 'number' && Number.isFinite(value)) present.push({ value, index });
  });
  const values = present.map((point) => point.value);
  const weekLabels = rawWeekLabels ? present.map((point) => rawWeekLabels[point.index] ?? '') : undefined;
  let selectedIndex: number | undefined;
  if (rawSelectedIndex != null) {
    let found = -1;
    present.forEach((point, i) => {
      if (point.index === rawSelectedIndex) found = i;
    });
    selectedIndex = found >= 0 ? found : undefined;
  }

  if (values.length === 0) return null;

  const x = values.length > 1
    ? (i: number) => PAD.left + (i / (values.length - 1)) * plotW
    : (_i: number) => PAD.left + plotW / 2;

  if (values.length === 1) {
    const yVal = PAD.top + plotH / 2;
    return (
      <div className="inline-trend-wrapper">
        <svg className="inline-trend" width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} fill="#f8f9fb" rx={4} />
          <circle cx={x(0)} cy={yVal} r={3} fill={color} stroke={color} strokeWidth={1.2} />
          <text x={x(0)} y={H - 3} fontSize={8} fill={color} fontWeight={700} textAnchor="middle">
            {(weekLabels?.[0] ?? 'W1').replace(/^\d{4}\s+Week\s+/, 'W')}
          </text>
        </svg>
      </div>
    );
  }

  const domain = goal != null ? [...values, goal] : values;
  let min = Math.min(...domain);
  let max = Math.max(...domain);
  const span = max - min || 1;
  min -= span * 0.2;
  max += span * 0.2;

  const y = (v: number) => PAD.top + (1 - (v - min) / (max - min)) * plotH;

  const pts = values.map((v, i) => ({ x: x(i), y: y(v) }));
  const linePath = smoothPath(pts);
  const areaPath = `${linePath} L ${pts[pts.length - 1].x.toFixed(1)} ${PAD.top + plotH} L ${pts[0].x.toFixed(1)} ${PAD.top + plotH} Z`;
  const hasGoal = goal != null;

  // Two gridlines at 33% and 66% height
  const gridY1 = PAD.top + plotH * 0.33;
  const gridY2 = PAD.top + plotH * 0.66;

  return (
    <div className="inline-trend-wrapper">
      <svg className="inline-trend" width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
          <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Plot area background */}
        <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} fill="#f8f9fb" rx={4} />

        {/* Y-axis: top (max) and bottom (min) value labels */}
        <text x={PAD.left - 4} y={PAD.top + 6} fontSize={7} fill="#9aa5b1" textAnchor="end">
          {fmt(Math.max(...values))}
        </text>
        <text x={PAD.left - 4} y={PAD.top + plotH - 1} fontSize={7} fill="#9aa5b1" textAnchor="end">
          {fmt(Math.min(...values))}
        </text>

        {/* Subtle gridlines */}
        <line x1={PAD.left} y1={gridY1} x2={W - PAD.right} y2={gridY1} stroke="#e8ebef" strokeWidth={0.5} />
        <line x1={PAD.left} y1={gridY2} x2={W - PAD.right} y2={gridY2} stroke="#e8ebef" strokeWidth={0.5} />

        {/* Goal threshold with label — label sits in the left padding gutter,
            vertically centered on the line so it never overlaps the series */}
        {hasGoal && (
          <g>
            <line
              x1={PAD.left} y1={y(goal)} x2={W - PAD.right} y2={y(goal)}
              stroke="#037f0c" strokeWidth={1} strokeDasharray="4 3" opacity={0.55}
            />
            <rect x={PAD.left + 2} y={y(goal) - 6} width={20} height={11} fill="#fff" opacity={0.85} rx={2} />
            <text x={PAD.left + 4} y={y(goal) + 3} fontSize={7} fill="#d91515" fontWeight={700} opacity={0.9}>
              {goal}%
            </text>
          </g>
        )}

        {/* Gradient area fill */}
        <path d={areaPath} fill={`url(#${gradId})`} />

        {/* Line with subtle glow */}
        <path d={linePath} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" filter={`url(#${glowId})`} opacity={0.4} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

        {/* Dots — static, no size/color change on hover */}
        {values.map((v, i) => {
          const isSelected = selectedIndex === i;
          return (
            <circle
              key={i}
              cx={x(i)} cy={y(v)}
              r={isSelected ? 2 : 1.5}
              fill={isSelected ? color : '#fff'}
              stroke={color}
              strokeWidth={1.2}
            />
          );
        })}

        {/* Hit targets */}
        {values.map((v, i) => (
          <circle
            key={`hit-${i}`}
            cx={x(i)} cy={y(v)} r={12}
            fill="transparent"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}

        {/* Week labels */}
        {values.map((_, i) => (
          <text
            key={i} x={x(i)} y={H - 3}
            fontSize={8}
            fill={selectedIndex === i ? color : '#9aa5b1'}
            fontWeight={selectedIndex === i ? 700 : 400}
            textAnchor="middle"
          >
            {(weekLabels?.[i] ?? `W${i + 1}`).replace(/^\d{4}\s+Week\s+/, 'W')}
          </text>
        ))}
      </svg>

      {hovered !== null && hovered < values.length && (
        <div className="inline-trend-tooltip" style={{ left: x(hovered), top: y(values[hovered]) - 30 }}>
          {fmt(values[hovered])}
        </div>
      )}
    </div>
  );
};

export default InlineTrend;
